import { getHostedIdentifySessionRequirementsOptions, patchHostedIdentifySessionVaultMutation, postHostedUserConsentMutation } from '@onefootprint/fetch-hosted';
import type { VaultData, VerifiedPrefillContext } from '@onefootprint/request-types';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { getConsentContent } from '@/idv2/components/consent-footer';
import { CollectPhone } from '@/idv2/features/identify/collect-phone';
import { isSessionExpired } from '@/idv2/features/identify/handle-session-expired';
import { useVaultErrorMessage } from '@/idv2/features/identify/use-vault-error-message';
import { useFootprint } from '@/idv2/hooks/use-footprint';
const phoneSearchSchema = z.object({
  email: z.string().optional(),
  phone: z.string().optional()
});
type PhoneLoaderData = {
  displayTermsAndConditions: boolean;
  verifiedPrefillContext: VerifiedPrefillContext | undefined;
};
export const Route = createFileRoute('/identify/phone')({
  validateSearch: phoneSearchSchema,
  loader: async ({
    context
  }): Promise<PhoneLoaderData> => {
    const headers = {
      'X-Fp-Authorization': context.onboardingSessionToken
    };
    const data = await context.queryClient.ensureQueryData(getHostedIdentifySessionRequirementsOptions({
      headers
    }));
    return {
      displayTermsAndConditions: data?.displayTermsAndConditions ?? false,
      verifiedPrefillContext: data?.verifiedPrefillContext
    };
  },
  component: PhonePage
});
function PhonePage() {
  const footprint = useFootprint();
  const navigate = useNavigate();
  const {
    onboardingSessionToken
  } = Route.useRouteContext();
  const {
    email,
    phone
  } = Route.useSearch();
  const {
    t
  } = useTranslation('idv2');
  const {
    displayTermsAndConditions,
    verifiedPrefillContext
  } = Route.useLoaderData();
  const getVaultErrorMessage = useVaultErrorMessage();
  const vaultMutation = useMutation(patchHostedIdentifySessionVaultMutation({
    headers: {
      'X-Fp-Authorization': onboardingSessionToken
    }
  }));
  const consentMutation = useMutation(postHostedUserConsentMutation({
    headers: {
      'X-Fp-Authorization': onboardingSessionToken
    }
  }));
  const handleSubmit = async (data: VaultData, onError: (message: string) => void) => {
    try {
      await vaultMutation.mutateAsync({
        body: data
      });
      if (verifiedPrefillContext?.isEnabled) {
        const collectsSsn = verifiedPrefillContext.collectsSsn ?? false;
        const content = getConsentContent(t, [{
          kind: 'verified_prefill',
          collectsSsn
        }]);
        await consentMutation.mutateAsync({
          body: {
            content,
            kind: 'verified_prefill'
          }
        });
      }
      footprint.navigation.forward();
    } catch (error) {
      if (isSessionExpired(error)) throw error;
      const message = getVaultErrorMessage(error, 'id.phone_number');
      if (message) onError(message);
    }
  };
  const handleChangeEmail = () => {
    navigate({
      to: '/identify/email',
      search: {
        email
      }
    });
  };
  return <CollectPhone onSubmit={handleSubmit} isLoading={vaultMutation.isPending || consentMutation.isPending} email={email} defaultPhone={phone} onChangeEmail={handleChangeEmail} showLegalFooter={displayTermsAndConditions} verifiedPrefillContext={verifiedPrefillContext} />;
}